import produce from "immer";
import React, { JSX } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography
} from "@mui/material";
import { startCase } from "lodash";
import { useSelector } from "react-redux";

import {
  deserializeNode,
  proseMirrorOptions,
  RichTextfield
} from "../../../../components/RichTextfield";
import {
  IBillingExtraColumns,
  ResourceCentre
} from "../../../../interfaces/ResourceCentreInterface";
import Can from "../../../Policy/Can";
import { RootState } from "../../../../store";
import * as billActions from "../../../../actions/bill";
import { tl } from "../../../../components/translate";
import BillPrintCustomHeader from "../BillPrintCustomHeader";
import HtmlTooltip from "../../../../components/HtmlTooltip";
import QrPaymentInfoForm from "../QrPayment/QrPaymentInfoForm";
import { notificationAdd } from "../../../../actions/notification";
import { resourceCentreActions as actions } from "../../../../actions";
import { useAppDispatch } from "../../../../store/hooks";
import { useParsedProseMirror } from "../../../Lab/LabEntry/LabTestSubRow";
import { customHeaderImagePresignedS3 } from "../../../../api/resourceCentre";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { DiscountBasedOn, DiscountTypes } from "../../../../interfaces/BillInterfaces";
import ContentEditableWithTextField from "../../../../components/ContentEditableWithTextField/ContentEditableWithTextField";

// eslint-disable-next-line import/prefer-default-export
export const BillSettings = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const resourceCentre: ResourceCentre | undefined = useCurrentResourceCentre();

  const billDraft = useSelector((state: RootState) => state.bills.draft);

  // refs
  const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // selectors
  const departmentSubscription = useSelector(
    (state: RootState) =>
      state.userContext.resourceCentre?.subscriptions.features.department?.subscribed
  );

  const [billDefaultRemarks, setBillDefaultRemarks] = useParsedProseMirror(
    resourceCentre?.settings?.billingSettings?.defaultRemarks || "",
    proseMirrorOptions
  );

  return (
    <Box>
      <Box width="100%" display="flex" alignItems="center">
        <Typography component="span" fontSize="14px" fontWeight="600" flexBasis="100px">
          {tl("vatRegistered")}
        </Typography>
        <Checkbox
          size="small"
          checked={resourceCentre?.settings?.billingSettings?.vatRegistered ?? false}
          value={resourceCentre?.settings?.billingSettings?.vatRegistered ?? false}
          onChange={() => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.billingSettings.vatRegistered =
                !draft.settings.billingSettings.vatRegistered;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
        />
      </Box>
      <Box width="100%" display="flex" alignItems="center">
        <ContentEditableWithTextField
          label={tl("billing.VAT")}
          placeholder="Type vat number here.."
          saveOnFocusOut
          value={String(resourceCentre?.settings?.billingSettings?.vatNo) ?? ""}
          disabled={!resourceCentre?.settings?.billingSettings.vatRegistered}
          onSave={(val) => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.billingSettings.vatNo = val;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
          width="300px"
        />
      </Box>
      <Box width="100%" display="flex" alignItems="center">
        <ContentEditableWithTextField
          label={tl("panNo")}
          placeholder="Type your pan number here.."
          saveOnFocusOut
          value={String(resourceCentre?.settings?.billingSettings?.panNo) ?? ""}
          onSave={(val) => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.billingSettings.panNo = val;
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
          width="300px"
        />
      </Box>

      {/* disocunt settings */}
      <Box width={1} display="flex" flexDirection="column">
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" style={{ color: "inherit" }}>
            {tl("billingSettings.discountBasis")}
          </FormLabel>
          <RadioGroup
            row
            name="discountBasis"
            value={
              resourceCentre?.settings?.billingSettings?.discountSettings?.discountBasis ||
              DiscountBasedOn.invoice
            }
            onChange={(e) => {
              const newRc = produce(resourceCentre, (draft) => {
                draft.settings.billingSettings.discountSettings.discountBasis = e.target
                  .value as DiscountBasedOn;
              });
              dispatch(actions.putResourceCentre(newRc));
              if (billDraft) {
                dispatch(
                  billActions.updateDraftSettings({
                    ...billDraft.settings,
                    discountSettings: {
                      ...billDraft.settings.discountSettings,
                      discountBasis: e.target.value as DiscountBasedOn
                    }
                  })
                );
              }
            }}
            sx={{ ml: 2 }}
          >
            <FormControlLabel
              value={DiscountBasedOn.invoice}
              control={<Radio />}
              label={tl("billingSettings.discountBasedOn.invoiceTotal")}
            />
            <FormControlLabel
              value={DiscountBasedOn.inline}
              control={<Radio />}
              label={tl("billingSettings.discountBasedOn.invoiceLine")}
            />
          </RadioGroup>
        </FormControl>
        <Box mt={2}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" style={{ color: "inherit" }}>
              {tl("billingSettings.discountType")}
            </FormLabel>
            <RadioGroup
              row
              name="discountType"
              value={
                resourceCentre?.settings?.billingSettings?.discountSettings?.discountType ??
                DiscountTypes.percentage
              }
              onChange={(e) => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.discountSettings.discountType = e.target
                    .value as DiscountTypes;
                });
                dispatch(actions.putResourceCentre(newRc));

                if (billDraft) {
                  dispatch(
                    billActions.updateDraftSettings({
                      ...billDraft.settings,
                      discountSettings: {
                        ...billDraft.settings.discountSettings,
                        discountType: e.target.value as DiscountTypes
                      }
                    })
                  );
                }
              }}
              sx={{ ml: 2 }}
            >
              <FormControlLabel
                value={DiscountTypes.percentage}
                control={<Radio />}
                label={tl("billingSettings.discountType.percentage")}
              />
              <FormControlLabel
                value={DiscountTypes.amount}
                control={<Radio />}
                label={tl("billingSettings.discountType.amount")}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>

      {/* show hide columns settings */}
      <Box mt="20px">
        <Typography component="span" fontSize="16px" fontWeight={600}>
          Show/Hide Columns
        </Typography>

        {(
          Object.keys(resourceCentre?.settings?.billingSettings?.extraColumns || {}) as Array<
            keyof IBillingExtraColumns
          >
        ).map((colName) => (
          <Box display="flex" alignItems="center" key={colName}>
            <Checkbox
              checked={resourceCentre?.settings?.billingSettings?.extraColumns?.[colName] ?? false}
              disabled={colName === "department" && !departmentSubscription}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.extraColumns[colName] =
                    !draft.settings.billingSettings.extraColumns[colName];
                });

                dispatch(actions.putResourceCentre(newRc));
                if (billDraft) {
                  dispatch(
                    billActions.updateDraftSettings({
                      ...billDraft.settings,
                      extraColumnSettings: {
                        ...billDraft.settings.extraColumnSettings,
                        [colName]: newRc.settings.billingSettings.extraColumns[colName]
                      }
                    })
                  );
                }
              }}
            />

            <Typography>{startCase(colName)}</Typography>
          </Box>
        ))}
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre.settings.billingSettings.syncWithIRD}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.syncWithIRD =
                    !draft.settings.billingSettings.syncWithIRD;
                });
                dispatch(actions.putResourceCentre(newRc));
              }}
            />
          }
          label={tl("settings.billSyncWithIRD")}
        />
      </Box>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre.settings.billingSettings.enableLabTestCreation}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.enableLabTestCreation =
                    !draft.settings.billingSettings.enableLabTestCreation;
                });
                dispatch(actions.putResourceCentre(newRc));
              }}
            />
          }
          label={tl("settings.enableLabTestCreationFromBill")}
        />
      </Box>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre?.settings?.billingSettings?.enableMedicalTestCreation}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.enableMedicalTestCreation =
                    !draft.settings.billingSettings.enableMedicalTestCreation;
                });
                dispatch(actions.putResourceCentre(newRc));
              }}
            />
          }
          label={tl("settings.enableMedicalTestCreationFromBill")}
        />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre.settings.billingSettings.enableCounterBilling}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.enableCounterBilling =
                    !draft.settings.billingSettings.enableCounterBilling;
                });
                dispatch(actions.putResourceCentre(newRc));
              }}
            />
          }
          label={tl("settings.enableCounterBilling")}
        />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre.settings.billingSettings.remarksCompulsoryForDiscountedBill}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.remarksCompulsoryForDiscountedBill =
                    !draft.settings.billingSettings.remarksCompulsoryForDiscountedBill;
                });
                dispatch(actions.putResourceCentre(newRc));
                if (billDraft) {
                  dispatch(
                    billActions.updateDraftSettings({
                      ...billDraft.settings,
                      remarksCompulsoryForDiscountedBill:
                        newRc.settings.billingSettings.remarksCompulsoryForDiscountedBill
                    })
                  );
                }
              }}
            />
          }
          label={tl("settings.remarksCompulsoryForDiscountedBill")}
        />
      </Box>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre.settings.billingSettings.showSubItemPriceOnPrint}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.showSubItemPriceOnPrint =
                    !draft.settings.billingSettings.showSubItemPriceOnPrint;
                });
                dispatch(actions.putResourceCentre(newRc));
                if (billDraft) {
                  dispatch(
                    billActions.updateDraftSettings({
                      ...billDraft.settings,
                      showSubItemPriceOnPrint:
                        newRc.settings.billingSettings.showSubItemPriceOnPrint
                    })
                  );
                }
              }}
            />
          }
          label={tl("settings.showSubItemPriceOnPrint")}
        />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre?.settings?.billingSettings?.requireSpInService ?? false}
              onChange={() => {
                dispatch(
                  actions.putResourceCentre(
                    produce(resourceCentre, (draft) => {
                      draft.settings.billingSettings.requireSpInService =
                        !resourceCentre.settings.billingSettings.requireSpInService;
                    })
                  )
                );
              }}
            />
          }
          label={tl("requireSpInService")}
        />
        <HtmlTooltip description="Service provider will be required in all services while creating bill. Not including service provider will stop bill from finalizing." />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={
                resourceCentre?.settings?.billingSettings?.allowServiceAndStockItemOnlyInBill ??
                false
              }
              onChange={() => {
                dispatch(
                  actions.putResourceCentre(
                    produce(resourceCentre, (draft) => {
                      draft.settings.billingSettings.allowServiceAndStockItemOnlyInBill =
                        !resourceCentre.settings.billingSettings.allowServiceAndStockItemOnlyInBill;
                    })
                  )
                );
              }}
            />
          }
          label={tl("allowServiceAndStockItemOnlyInBill")}
        />
        <HtmlTooltip description="If enabled, only service and stock items can be added to the bill." />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={
                resourceCentre?.settings?.billingSettings?.showSpSelectInBillItemsAllTheTime ??
                false
              }
              onChange={() => {
                dispatch(
                  actions.putResourceCentre(
                    produce(resourceCentre, (draft) => {
                      draft.settings.billingSettings.showSpSelectInBillItemsAllTheTime =
                        !resourceCentre.settings.billingSettings.showSpSelectInBillItemsAllTheTime;
                    })
                  )
                );
              }}
            />
          }
          label={tl("showSpSelectInBillItemsAllTheTime")}
        />
        <HtmlTooltip description="If enabled, the service provider selection will be shown for all bill items all the time." />
      </Box>

      <Box mt="8px">
        <Typography fontWeight={600} fontSize="14px">
          {tl("module.remindOn")}
        </Typography>
        <Box display="flex" width="100%" alignItems="center">
          <Checkbox
            size="small"
            checked={resourceCentre.settings.billingSettings.showReminder}
            value={resourceCentre.settings.billingSettings.showReminder}
            onChange={async () => {
              const newRc = produce(resourceCentre, (draft) => {
                draft.settings.billingSettings.showReminder =
                  !resourceCentre.settings.billingSettings.showReminder;
              });
              dispatch(actions.putResourceCentre(newRc));
            }}
          />
          <Typography>Show Remind On</Typography>
          <HtmlTooltip description="If enabled, reminders will be shown for bills." />
        </Box>
      </Box>
      <Box mt="8px">
        <Typography fontWeight={600} fontSize="14px">
          {tl("billing.defaultRemarks")}
        </Typography>
        <Box width="400px" ml={2} mt={0.5}>
          <RichTextfield
            state={billDefaultRemarks}
            setState={setBillDefaultRemarks}
            minHeight={70}
            showBoldItalicUnderlineOnly
            onChange={() => {
              if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
              }

              debounceTimeout.current = setTimeout(() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.billingSettings.defaultRemarks = deserializeNode(
                    billDefaultRemarks.doc
                  );
                });

                dispatch(actions.putResourceCentre(newRc));
              }, 1000);
            }}
          />
        </Box>
      </Box>
      <Box mt="8px">
        <Typography style={{ fontWeight: 600 }}>{tl("settings.billPrint")}</Typography>
        <Box pl="16px">
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={
                    resourceCentre.settings.printSettings.billPrintSettings.includeLetterhead
                  }
                  onChange={() => {
                    const newRc = produce(resourceCentre, (draft) => {
                      draft.settings.printSettings.billPrintSettings.includeLetterhead =
                        !resourceCentre.settings.printSettings.billPrintSettings.includeLetterhead;
                    });
                    dispatch(actions.putResourceCentre(newRc));
                  }}
                />
              }
              label={tl("settings.includeLetterhead")}
            />
          </Box>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={resourceCentre.settings.printSettings.billPrintSettings.includeFooter}
                  onChange={() => {
                    const newRc = produce(resourceCentre, (draft) => {
                      draft.settings.printSettings.billPrintSettings.includeFooter =
                        !resourceCentre.settings.printSettings.billPrintSettings.includeFooter;
                    });
                    dispatch(actions.putResourceCentre(newRc));
                  }}
                />
              }
              label={tl("settings.includeFooter")}
            />
            {!resourceCentre.settings.printSettings.billPrintSettings.includeFooter && (
              <Can policyAccessKey="deny" superAdminPass>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        resourceCentre.settings.printSettings.billPrintSettings
                          .hideFooterLogoCompletely
                      }
                      onChange={() => {
                        const newRc = produce(resourceCentre, (draft) => {
                          draft.settings.printSettings.billPrintSettings.hideFooterLogoCompletely =
                            !resourceCentre.settings.printSettings.billPrintSettings
                              .hideFooterLogoCompletely;
                        });
                        dispatch(actions.putResourceCentre(newRc));
                      }}
                    />
                  }
                  label={tl("settings.hideFooterCompletely")}
                />
              </Can>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={resourceCentre.settings.printSettings.includeReferrer}
                  onChange={() => {
                    const newRc = produce(resourceCentre, (draft) => {
                      draft.settings.printSettings.includeReferrer =
                        !draft.settings.printSettings.includeReferrer;
                    });
                    dispatch(actions.putResourceCentre(newRc));
                  }}
                />
              }
              label={tl("settings.includeReferrer")}
            />
          </Box>
        </Box>
        <Box my={2} border="1px solid #E0E0E0" p={1} pb={0} borderRadius={2} maxWidth={600}>
          <Typography fontWeight={600} fontSize={15} mb={1}>
            A5 Template Settings
          </Typography>
          <ContentEditableWithTextField
            label={tl("settings.customHeaderTitle")}
            placeholder="Custom bill print title"
            saveOnFocusOut
            value={resourceCentre.settings.printSettings.billPrintSettings.customTitle}
            onSave={(val) => {
              const newRc = produce(resourceCentre, (draft) => {
                draft.settings.printSettings.billPrintSettings.customTitle = val || "";
              });
              dispatch(actions.putResourceCentre(newRc));
            }}
            width="300px"
          />
          <ContentEditableWithTextField
            label={tl("settings.customHeaderSubTitle")}
            placeholder="Custom bill sub title"
            saveOnFocusOut
            value={resourceCentre.settings.printSettings.billPrintSettings.customSubtitle}
            onSave={(val) => {
              const newRc = produce(resourceCentre, (draft) => {
                draft.settings.printSettings.billPrintSettings.customSubtitle = val || "";
              });
              dispatch(actions.putResourceCentre(newRc));
            }}
            width="300px"
          />
        </Box>

        <BillPrintCustomHeader
          ratio={{ x: 4.936, y: 1 }}
          title="Custom Print Header (888px * 180)"
          description="This image will appear at the top of default, border and a5 templates"
          currentImage={resourceCentre.settings.billingSettings.customHeaderImage}
          onRemove={() => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.billingSettings.customHeaderImage = "";
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
          onSave={async (img) => {
            try {
              const data = await customHeaderImagePresignedS3(img);
              if (!data) {
                return;
              }
              const newRc = produce(resourceCentre, (draft) => {
                draft.settings.billingSettings.customHeaderImage = data;
              });
              dispatch(actions.putResourceCentre(newRc));
            } catch (err) {
              dispatch(
                notificationAdd({
                  id: new Date().getUTCMilliseconds(),
                  variant: "error",
                  message: "Failed to upload custom header",
                  autoTimeout: true
                })
              );
            }
          }}
        />
      </Box>
      <Box mt="20px">
        <QrPaymentInfoForm resourceCentre={resourceCentre} />
      </Box>
    </Box>
  );
};
